import { CSSProperties, MouseEvent, TouchEvent, useEffect, useRef, useState } from 'react';

import moment from 'moment';

const SLIDE_DURATION_MS = 300;
/** Доля ширины, после которой отпущенный свайп листает неделю, а не возвращается */
const SWIPE_THRESHOLD = 0.2;
/** Сдвиг пальца, после которого понятно, листают ли неделю или скроллят часы */
const AXIS_LOCK_PX = 8;

/** Куда едет лента: на неделю назад, остаётся или на неделю вперёд */
type TSlide = -1 | 0 | 1;

type TTouch = { x: number; y: number; axis: 'x' | 'y' | null };

const getWeekStart = (date?: moment.Moment | null) => (date ?? moment()).clone().startOf('isoWeek');

/**
 * Листание недель как в галерее: лента из трёх недель (прошлая, текущая,
 * следующая) тянется за пальцем и доезжает анимацией. Стрелки листают так же.
 * Конец анимации ловим таймером, а не `transitionend`: в фоновой вкладке
 * анимации заморожены и событие может не прийти.
 */
export const useWeekCarousel = (initialDate?: moment.Moment | null) => {
    const [weekStart, setWeekStart] = useState(() => getWeekStart(initialDate));
    const [slide, setSlide] = useState<TSlide>(0);
    const [dragOffset, setDragOffset] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);

    const viewportRef = useRef<HTMLDivElement>(null);
    const touch = useRef<TTouch | null>(null);
    const wasDragged = useRef(false);
    const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    useEffect(() => () => clearTimeout(timer.current), []);

    const slideTo = (target: TSlide) => {
        if (isAnimating) return;
        setIsAnimating(true);
        setSlide(target);
        setDragOffset(0);

        timer.current = setTimeout(() => {
            // Одним рендером: неделя сменилась, лента без анимации вернулась в центр
            setIsAnimating(false);
            setSlide(0);
            if (target !== 0) setWeekStart((prev) => prev.clone().add(target, 'week'));
        }, SLIDE_DURATION_MS);
    };

    /** Прыжок без анимации — на сегодня или на неделю даты, выбранной вне сетки */
    const jumpTo = (date?: moment.Moment) => {
        if (isAnimating) return;
        setWeekStart(getWeekStart(date));
    };

    const onTouchStart = (event: TouchEvent) => {
        if (isAnimating) return;
        touch.current = { x: event.touches[0].clientX, y: event.touches[0].clientY, axis: null };
        wasDragged.current = false;
    };

    const onTouchMove = (event: TouchEvent) => {
        const start = touch.current;
        if (!start) return;
        const deltaX = event.touches[0].clientX - start.x;
        const deltaY = event.touches[0].clientY - start.y;

        if (!start.axis && Math.max(Math.abs(deltaX), Math.abs(deltaY)) > AXIS_LOCK_PX) {
            start.axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
        }
        if (start.axis !== 'x') return;

        wasDragged.current = true;
        setDragOffset(deltaX);
    };

    const onTouchEnd = () => {
        const start = touch.current;
        touch.current = null;
        if (start?.axis !== 'x') return;

        const width = viewportRef.current?.offsetWidth ?? 0;
        const isFarEnough = Math.abs(dragOffset) > width * SWIPE_THRESHOLD;
        slideTo(isFarEnough ? (dragOffset < 0 ? 1 : -1) : 0);
    };

    // Палец, который тянул ленту, не должен заодно выбрать ячейку
    const onClickCapture = (event: MouseEvent) => {
        if (!wasDragged.current) return;
        wasDragged.current = false;
        event.stopPropagation();
    };

    const trackStyle: CSSProperties = {
        transform: `translateX(calc(${-100 - slide * 100}% + ${dragOffset}px))`,
        transition: isAnimating ? `transform ${SLIDE_DURATION_MS}ms ease-out` : 'none',
    };

    return {
        weekStart,
        prevWeek: () => slideTo(-1),
        nextWeek: () => slideTo(1),
        toToday: () => jumpTo(),
        showWeekOf: (date: moment.Moment) => jumpTo(date),
        trackStyle,
        viewportProps: { ref: viewportRef, onTouchStart, onTouchMove, onTouchEnd, onClickCapture },
    };
};

export type TWeekCarousel = ReturnType<typeof useWeekCarousel>;
